import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { RichTextDisplay, RichTextEditor, stripHtml } from '@/components/RichTextEditor';
import { ChevronLeft, Upload, FileText, Image, File, X, Eye, Download, ZoomIn, ZoomOut, FlaskConical, Plus, Printer, Sparkles, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// File size formatter
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Get file icon based on type
const getFileIcon = (tipo) => {
  const imageTypes = ['jpg', 'jpeg', 'png'];
  if (imageTypes.includes(tipo?.toLowerCase())) {
    return <Image className="w-5 h-5 text-green-600" />;
  }
  if (tipo?.toLowerCase() === 'pdf') {
    return <FileText className="w-5 h-5 text-red-600" />;
  }
  return <File className="w-5 h-5 text-blue-600" />;
};

function ProntuarioPage({ user, onLogout, showConfig }) {
  const { pacienteId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  
  // State
  const [paciente, setPaciente] = useState(null);
  const [prontuarios, setProntuarios] = useState([]);
  const [loadingProntuarios, setLoadingProntuarios] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('novaQueixa') === 'true' ? 'queixas' : 'historico');
  
  // Queixas state - inicializa com base no parâmetro da URL
  const [showNovaQueixa, setShowNovaQueixa] = useState(searchParams.get('novaQueixa') === 'true');
  const [novaQueixa, setNovaQueixa] = useState('');
  const [salvandoQueixa, setSalvandoQueixa] = useState(false);
  
  // IA Assistente state
  const [iaHabilitado, setIaHabilitado] = useState(false);
  const [iaLoading, setIaLoading] = useState(false);
  const [iaResposta, setIaResposta] = useState(null);
  const [iaQueixaSelecionada, setIaQueixaSelecionada] = useState(null);
  
  // Limpar parâmetro da URL após ler
  const toastShownRef = useRef(false);
  useEffect(() => {
    if (searchParams.get('novaQueixa') && !toastShownRef.current) {
      toastShownRef.current = true;
      setSearchParams({}, { replace: true });
      toast.success('Atendimento iniciado! Registre a queixa do paciente.');
    }
  }, []);

  // Proteção contra navegação com queixa não salva
  const hasUnsavedQueixa = showNovaQueixa && novaQueixa.length > 0;
  const hasUnsavedRef = useRef(false);
  const [showSairSemSalvarModal, setShowSairSemSalvarModal] = useState(false);
  const [navegacaoPendente, setNavegacaoPendente] = useState(null);

  useEffect(() => {
    hasUnsavedRef.current = hasUnsavedQueixa;
  }, [hasUnsavedQueixa]);

  // Interceptar botão voltar do navegador
  useEffect(() => {
    if (!hasUnsavedQueixa) return;

    // Push extra state to detect back button
    window.history.pushState({ queixaGuard: true }, '');

    const handlePopState = (e) => {
      if (hasUnsavedRef.current) {
        // Re-push to prevent navigation
        window.history.pushState({ queixaGuard: true }, '');
        setNavegacaoPendente('/pacientes');
        setShowSairSemSalvarModal(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [hasUnsavedQueixa]);

  // Interceptar fechar aba/janela
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedQueixa) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedQueixa]);

  // Callback para o Layout interceptar cliques no sidebar
  const handleBeforeNavigate = useCallback((path) => {
    if (hasUnsavedRef.current) {
      setNavegacaoPendente(path);
      setShowSairSemSalvarModal(true);
      return false;
    }
    return true;
  }, []);

  const confirmarSairSemSalvar = () => {
    const destino = navegacaoPendente;
    setShowSairSemSalvarModal(false);
    setShowNovaQueixa(false);
    setNovaQueixa('');
    setNavegacaoPendente(null);
    if (destino === '__logout__') {
      onLogout();
      return;
    }
    if (destino) {
      navigate(destino);
    }
  };

  const salvarENavegar = async () => {
    const destino = navegacaoPendente;
    const salvou = await handleSalvarQueixa();
    if (!salvou) return;

    setShowSairSemSalvarModal(false);
    setNavegacaoPendente(null);
    if (destino === '__logout__') {
      onLogout();
      return;
    }
    if (destino) {
      navigate(destino);
    }
  };

  const fecharModalQueixaNaoSalva = () => {
    setShowSairSemSalvarModal(false);
    setNavegacaoPendente(null);
  };
  
  // Arquivos state
  const [arquivos, setArquivos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [descricaoArquivo, setDescricaoArquivo] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Visualização de arquivo
  const [viewingFile, setViewingFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const [zoom, setZoom] = useState(1);

  // Solicitar Exames state (separado por categoria)
  const [examesClinicos, setExamesClinicos] = useState([]);
  const [examesImagem, setExamesImagem] = useState([]);
  const [categoriaExameAtiva, setCategoriaExameAtiva] = useState('clinico');
  const [examesSelecionadosClinico, setExamesSelecionadosClinico] = useState([]);
  const [examesSelecionadosImagem, setExamesSelecionadosImagem] = useState([]);
  const [examesManuaisClinico, setExamesManuaisClinico] = useState([]);
  const [examesManuaisImagem, setExamesManuaisImagem] = useState([]);
  const [novoExameManualClinico, setNovoExameManualClinico] = useState('');
  const [novoExameManualImagem, setNovoExameManualImagem] = useState('');
  const [observacoesClinico, setObservacoesClinico] = useState('');
  const [observacoesImagem, setObservacoesImagem] = useState('');
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [examesCarregados, setExamesCarregados] = useState(false);
  const [loadingExames, setLoadingExames] = useState(false);

  // Receituário state
  const [modelosReceituario, setModelosReceituario] = useState([]);
  const [conteudoReceituario, setConteudoReceituario] = useState('');
  const [tipoReceituario, setTipoReceituario] = useState('receituario');
  const [nomeModeloSelecionado, setNomeModeloSelecionado] = useState('');
  const [showEditorReceituario, setShowEditorReceituario] = useState(false);
  const [gerandoPdfReceituario, setGerandoPdfReceituario] = useState(false);
  const [modelosCarregados, setModelosCarregados] = useState(false);
  const [loadingModelos, setLoadingModelos] = useState(false);
  const [modeloSelecionandoId, setModeloSelecionandoId] = useState(null);

  useEffect(() => {
    loadPaciente();
  }, [pacienteId]);

  useEffect(() => {
    if (activeTab === 'arquivos') {
      loadArquivos();
    }
    if (activeTab === 'exames') {
      loadExamesCadastrados();
    }
    if (activeTab === 'receituario') {
      loadModelosReceituario();
    }
  }, [activeTab, pacienteId]);

  const loadExamesCadastrados = async () => {
    if (examesCarregados || loadingExames) return;
    setLoadingExames(true);
    try {
      const response = await axios.get(`${API}/exames`);
      const exames = response.data || [];
      setExamesClinicos(exames.filter(e => (e.categoria || 'clinico') === 'clinico'));
      setExamesImagem(exames.filter(e => e.categoria === 'imagem'));
      setExamesCarregados(true);
    } catch (error) {
      console.error('Erro ao carregar exames:', error);
    } finally {
      setLoadingExames(false);
    }
  };

  const loadModelosReceituario = async () => {
    if (modelosCarregados || loadingModelos) return;
    setLoadingModelos(true);
    try {
      const response = await axios.get(`${API}/modelos-receituario`, { params: { resumo: true } });
      setModelosReceituario(response.data);
      setModelosCarregados(true);
    } catch (error) {
      console.error('Erro ao carregar modelos:', error);
    } finally {
      setLoadingModelos(false);
    }
  };

  const handleSelecionarModelo = async (modelo) => {
    setModeloSelecionandoId(modelo.id);
    try {
      const completo = modelo.conteudo
        ? modelo
        : (await axios.get(`${API}/modelos-receituario/${modelo.id}`)).data;
      setConteudoReceituario(completo.conteudo || '');
      setTipoReceituario(completo.tipo);
      setNomeModeloSelecionado(completo.nome);
      setShowEditorReceituario(true);
    } catch (error) {
      console.error('Erro ao carregar modelo:', error);
      toast.error('Erro ao carregar modelo');
    } finally {
      setModeloSelecionandoId(null);
    }
  };

  const handleNovoReceituario = () => {
    setConteudoReceituario('');
    setTipoReceituario('receituario');
    setNomeModeloSelecionado('');
    setShowEditorReceituario(true);
  };

  const handleGerarPdfReceituario = async () => {
    if (!stripHtml(conteudoReceituario)) {
      toast.error('Digite o conteúdo do documento');
      return;
    }
    setGerandoPdfReceituario(true);
    try {
      const response = await axios.post(`${API}/receituario/gerar-pdf`, {
        paciente_id: pacienteId,
        conteudo: conteudoReceituario,
        tipo: tipoReceituario,
        nome_modelo: nomeModeloSelecionado
      }, { responseType: 'blob' });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Open in built-in viewer
      const tipoNome = tipoReceituario === 'receituario'
        ? 'Receituário'
        : tipoReceituario === 'orientacao'
          ? 'Orientação'
          : tipoReceituario === 'livre'
            ? 'Modelo Livre'
            : 'Atestado';
      setViewingFile({ nome_original: `${tipoNome} - ${paciente?.nome_completo || 'Paciente'}`, tipo: 'pdf' });
      setFileUrl(url);
      
      toast.success('Documento gerado com sucesso!');
      setShowEditorReceituario(false);
      setConteudoReceituario('');
      setNomeModeloSelecionado('');
      loadPaciente();
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar documento. Verifique se os dados do médico estão preenchidos nas Configurações.');
    } finally {
      setGerandoPdfReceituario(false);
    }
  };

  const toggleExameSelecionado = (nomeExame) => {
    if (categoriaExameAtiva === 'imagem') {
      setExamesSelecionadosImagem(prev =>
        prev.includes(nomeExame) ? prev.filter(e => e !== nomeExame) : [...prev, nomeExame]
      );
    } else {
      setExamesSelecionadosClinico(prev =>
        prev.includes(nomeExame) ? prev.filter(e => e !== nomeExame) : [...prev, nomeExame]
      );
    }
  };

  const adicionarExameManual = ({ silentDuplicate = false } = {}) => {
    if (categoriaExameAtiva === 'imagem') {
      const nome = novoExameManualImagem.trim();
      if (!nome) return;
      if (examesManuaisImagem.includes(nome)) {
        if (!silentDuplicate) toast.error('Este exame já foi adicionado');
        setNovoExameManualImagem('');
        return;
      }
      setExamesManuaisImagem(prev => [...prev, nome]);
      setNovoExameManualImagem('');
    } else {
      const nome = novoExameManualClinico.trim();
      if (!nome) return;
      if (examesManuaisClinico.includes(nome)) {
        if (!silentDuplicate) toast.error('Este exame já foi adicionado');
        setNovoExameManualClinico('');
        return;
      }
      setExamesManuaisClinico(prev => [...prev, nome]);
      setNovoExameManualClinico('');
    }
  };

  const adicionarExameManualAoSairDoCampo = () => {
    adicionarExameManual({ silentDuplicate: true });
  };

  const removerExameManual = (nome) => {
    if (categoriaExameAtiva === 'imagem') {
      setExamesManuaisImagem(prev => prev.filter(e => e !== nome));
    } else {
      setExamesManuaisClinico(prev => prev.filter(e => e !== nome));
    }
  };

  // Baixa o PDF retornado pelo endpoint como blob e dispara download automático
  const _downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const handleGerarGuia = async () => {
    const pendenteClinico = novoExameManualClinico.trim();
    const pendenteImagem = novoExameManualImagem.trim();
    const manuaisClinicoParaGuia = pendenteClinico && !examesManuaisClinico.includes(pendenteClinico)
      ? [...examesManuaisClinico, pendenteClinico]
      : examesManuaisClinico;
    const manuaisImagemParaGuia = pendenteImagem && !examesManuaisImagem.includes(pendenteImagem)
      ? [...examesManuaisImagem, pendenteImagem]
      : examesManuaisImagem;

    if (pendenteClinico) {
      setExamesManuaisClinico(manuaisClinicoParaGuia);
      setNovoExameManualClinico('');
    }
    if (pendenteImagem) {
      setExamesManuaisImagem(manuaisImagemParaGuia);
      setNovoExameManualImagem('');
    }

    const temClinico = examesSelecionadosClinico.length > 0 || manuaisClinicoParaGuia.length > 0;
    const temImagem = examesSelecionadosImagem.length > 0 || manuaisImagemParaGuia.length > 0;

    if (!temClinico && !temImagem) {
      toast.error('Selecione pelo menos um exame');
      return;
    }

    setGerandoPdf(true);
    try {
      // Monta as seções (uma por categoria com exames selecionados)
      const secoes = [];
      if (temClinico) {
        secoes.push({
          categoria: 'clinico',
          exames_selecionados: examesSelecionadosClinico,
          exames_manuais: manuaisClinicoParaGuia,
          observacoes: observacoesClinico,
        });
      }
      if (temImagem) {
        secoes.push({
          categoria: 'imagem',
          exames_selecionados: examesSelecionadosImagem,
          exames_manuais: manuaisImagemParaGuia,
          observacoes: observacoesImagem,
        });
      }

      const response = await axios.post(`${API}/exames/gerar-guia`, {
        paciente_id: pacienteId,
        secoes,
      }, { responseType: 'blob' });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const nomeSeguro = (paciente?.nome_completo || 'paciente').replace(/\s+/g, '_');
      const dataStr = new Date().toISOString().slice(0, 10);
      const filename = secoes.length === 1
        ? `guia_exames_${secoes[0].categoria === 'imagem' ? 'imagem' : 'clinicos'}_${nomeSeguro}_${dataStr}.pdf`
        : `guia_exames_${nomeSeguro}_${dataStr}.pdf`;
      _downloadBlob(blob, filename);

      toast.success(secoes.length === 2
        ? 'Guia gerada com sucesso (1 arquivo, 2 páginas)!'
        : 'Guia gerada com sucesso!');

      // Limpar formulário completo
      setExamesSelecionadosClinico([]);
      setExamesSelecionadosImagem([]);
      setExamesManuaisClinico([]);
      setExamesManuaisImagem([]);
      setNovoExameManualClinico('');
      setNovoExameManualImagem('');
      setObservacoesClinico('');
      setObservacoesImagem('');
      // Atualizar histórico
      loadPaciente();
    } catch (error) {
      console.error('Erro ao gerar guia:', error);
      toast.error('Erro ao gerar guia de exames. Verifique se os dados do médico estão preenchidos nas Configurações.');
    } finally {
      setGerandoPdf(false);
    }
  };

  const loadPaciente = async () => {
    try {
      setLoading(true);
      const pacienteResponse = await axios.get(`${API}/pacientes/${pacienteId}/basico`);
      setPaciente({ ...pacienteResponse.data, prontuarios: [] });
      setProntuarios([]);
      setLoading(false);
      loadProntuarios();
    } catch (error) {
      console.error('Erro ao carregar paciente:', error);
      toast.error('Erro ao carregar dados do paciente');
      navigate('/pacientes');
    } finally {
      setLoading(false);
    }
  };

  const loadProntuarios = async () => {
    setLoadingProntuarios(true);
    try {
      const response = await axios.get(`${API}/pacientes/${pacienteId}/prontuarios`, { params: { limit: 100 } });
      const prontuariosData = response.data || [];
      setProntuarios(prontuariosData);
      setPaciente(prev => prev ? { ...prev, prontuarios: prontuariosData } : prev);
    } catch (error) {
      console.error('Erro ao carregar prontuários:', error);
      toast.error('Erro ao carregar histórico do prontuário');
    } finally {
      setLoadingProntuarios(false);
    }
  };

  const loadArquivos = async () => {
    try {
      const response = await axios.get(`${API}/pacientes/${pacienteId}/arquivos`);
      setArquivos(response.data.arquivos || []);
    } catch (error) {
      console.error('Erro ao carregar arquivos:', error);
      toast.error('Erro ao carregar arquivos');
    }
  };

  // Check if IA is enabled for this client
  useEffect(() => {
    const checkIa = async () => {
      try {
        const resp = await axios.get(`${API}/auth/me`);
        setIaHabilitado(resp.data.ia_assistente_habilitado === true);
      } catch {}
    };
    checkIa();
  }, []);

  const handleConsultarIA = async (queixaTexto) => {
    setIaLoading(true);
    setIaResposta(null);
    setIaQueixaSelecionada(queixaTexto);
    try {
      const response = await axios.post(`${API}/ia/consultar`, {
        queixa: queixaTexto,
        paciente_info: paciente ? {
          nome: paciente.nome_completo,
          idade: paciente.data_nascimento ? Math.floor((new Date() - new Date(paciente.data_nascimento)) / 31557600000) : null
        } : {}
      });
      setIaResposta(response.data.resposta);
    } catch (error) {
      toast.error('Erro ao consultar assistente de IA');
      console.error(error);
    } finally {
      setIaLoading(false);
    }
  };

  // ============ QUEIXAS HANDLERS ============
  const handleSalvarQueixa = async () => {
    if (!novaQueixa.trim()) {
      toast.error('Por favor, preencha a queixa');
      return false;
    }

    setSalvandoQueixa(true);
    try {
      const novoProntuario = {
        id: `queixa-${Date.now()}`,
        data: new Date().toISOString(),
        conteudo: novaQueixa.trim(),
        criado_por: user?.nome_completo || 'Médico'
      };

      await axios.post(`${API}/pacientes/${pacienteId}/prontuarios`, novoProntuario);

      toast.success('Queixa salva com sucesso!');
      setNovaQueixa('');
      setShowNovaQueixa(false);
      await loadProntuarios();
      return true;
    } catch (error) {
      console.error('Erro ao salvar queixa:', error);
      toast.error('Erro ao salvar queixa');
      return false;
    } finally {
      setSalvandoQueixa(false);
    }
  };

  const handleExcluirQueixa = async (queixaId) => {
    if (!window.confirm('Deseja realmente excluir esta queixa?')) {
      return;
    }

    try {
      await axios.delete(`${API}/pacientes/${pacienteId}/prontuarios/${queixaId}`);

      toast.success('Queixa excluída com sucesso!');
      await loadProntuarios();
    } catch (error) {
      console.error('Erro ao excluir queixa:', error);
      toast.error('Erro ao excluir queixa');
    }
  };

  // ============ ARQUIVOS HANDLERS ============
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png'];
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedExtensions.includes(fileExt)) {
      toast.error('Tipo de arquivo não permitido. Formatos aceitos: PDF, DOC, JPG, JPEG, PNG');
      e.target.value = '';
      return;
    }

    // Validate file size (400MB)
    if (file.size > 400 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Tamanho máximo: 400MB');
      e.target.value = '';
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Selecione um arquivo');
      return;
    }

    if (descricaoArquivo.length > 60) {
      toast.error('Descrição deve ter no máximo 60 caracteres');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (descricaoArquivo.trim()) {
        formData.append('descricao', descricaoArquivo.trim());
      }

      await axios.post(`${API}/pacientes/${pacienteId}/arquivos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      toast.success('Arquivo enviado com sucesso!');
      setSelectedFile(null);
      setDescricaoArquivo('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      loadArquivos();
    } catch (error) {
      console.error('Erro ao enviar arquivo:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar arquivo');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (arquivo) => {
    try {
      // If the file is already loaded in the viewer (generated PDF), download from memory
      if (fileUrl && viewingFile && viewingFile === arquivo && !arquivo.id?.includes('-')) {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', arquivo.nome_original || 'documento.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
        return;
      }
      
      const response = await axios.get(
        `${API}/pacientes/${pacienteId}/arquivos/${arquivo.id}/download`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', arquivo.nome_original);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Fallback: if backend fails but we have fileUrl, use it
      if (fileUrl) {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', arquivo.nome_original || 'documento.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        console.error('Erro ao baixar arquivo:', error);
        toast.error('Erro ao baixar arquivo');
      }
    }
  };

  const handleDeleteArquivo = async (arquivoId) => {
    if (!window.confirm('Deseja realmente excluir este arquivo?')) {
      return;
    }

    try {
      await axios.delete(`${API}/pacientes/${pacienteId}/arquivos/${arquivoId}`);
      toast.success('Arquivo excluído com sucesso!');
      loadArquivos();
    } catch (error) {
      console.error('Erro ao excluir arquivo:', error);
      toast.error('Erro ao excluir arquivo');
    }
  };

  // Visualizar arquivo
  const handleViewFile = async (arquivo) => {
    const imageTypes = ['jpg', 'jpeg', 'png'];
    const tipo = arquivo.tipo?.toLowerCase();
    
    // Verificar se é um tipo suportado para visualização
    if (!imageTypes.includes(tipo) && tipo !== 'pdf') {
      toast.info('Este tipo de arquivo não pode ser visualizado. Use o botão Baixar.');
      return;
    }
    
    setLoadingFile(true);
    setViewingFile(arquivo);
    setZoom(1);
    
    try {
      const response = await axios.get(
        `${API}/pacientes/${pacienteId}/arquivos/${arquivo.id}/download`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { 
        type: imageTypes.includes(tipo) ? `image/${tipo}` : 'application/pdf' 
      });
      const url = window.URL.createObjectURL(blob);
      setFileUrl(url);
    } catch (error) {
      console.error('Erro ao carregar arquivo:', error);
      toast.error('Erro ao carregar arquivo para visualização');
      setViewingFile(null);
    } finally {
      setLoadingFile(false);
    }
  };

  // Fechar visualização
  const closeViewer = () => {
    if (fileUrl) {
      window.URL.revokeObjectURL(fileUrl);
    }
    setViewingFile(null);
    setFileUrl(null);
    setZoom(1);
  };

  // Zoom controls
  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));

  // ============ HELPERS ============
  const formatDateTime = (isoString) => {
    if (!isoString) return {};
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return { day, month, year, hours, minutes };
  };

  const getMonthName = (month) => {
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return months[parseInt(month) - 1];
  };

  const prontuariosOrdenados = (prontuarios || [])
    .sort((a, b) => new Date(b.data) - new Date(a.data));

  const arquivosOrdenados = [...arquivos].sort((a, b) => 
    new Date(b.data_upload) - new Date(a.data_upload)
  );

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} showConfig={showConfig}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} showConfig={showConfig} onBeforeNavigate={handleBeforeNavigate}>
        <div className="space-y-6" data-testid="prontuario-page">
        {/* Header */}
        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              if (hasUnsavedRef.current) {
                setNavegacaoPendente('/pacientes');
                setShowSairSemSalvarModal(true);
              } else {
                navigate('/pacientes');
              }
            }}
            className="text-gray-600 hover:text-gray-900"
            data-testid="btn-voltar"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Prontuário</h1>
            <p className="text-gray-600 mt-1">Gerencie queixas e arquivos do paciente</p>
          </div>
        </div>

        {/* Dados do Paciente */}
        <div className="bg-white rounded-lg shadow p-6 border-2 border-gray-300">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Dados do paciente</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">Nome Completo</p>
              <p className="text-base font-medium text-gray-900">{paciente?.nome_completo}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">CPF</p>
              <p className="text-base font-medium text-gray-900">{paciente?.cpf ? (() => { const d = (paciente.cpf || '').replace(/\D/g, '').slice(0, 11); if (d.length <= 3) return d; if (d.length <= 6) return `${d.slice(0,3)}.${d.slice(3)}`; if (d.length <= 9) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`; return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`; })() : '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Telefone</p>
              <p className="text-base font-medium text-gray-900">{paciente?.telefone || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Data de Nascimento</p>
              <p className="text-base font-medium text-gray-900">
                {paciente?.data_nascimento ? (() => {
                  const [year, month, day] = paciente.data_nascimento.split('-');
                  return `${day}/${month}/${year}`;
                })() : '-'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Convênio</p>
              <p className="text-base font-medium text-gray-900 capitalize">{paciente?.convenio || 'Particular'}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex space-x-4 overflow-x-auto">
            <button
              onClick={() => setActiveTab('historico')}
              className={`px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'historico'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="tab-historico"
            >
              Histórico
            </button>
            <button
              onClick={() => setActiveTab('queixas')}
              className={`px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'queixas'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="tab-queixas"
            >
              Queixas
            </button>
            <button
              onClick={() => setActiveTab('arquivos')}
              className={`px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'arquivos'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="tab-arquivos"
            >
              Resultado Exames
            </button>
            <button
              onClick={() => setActiveTab('exames')}
              className={`px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'exames'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="tab-exames"
            >
              Solicitar Exames
            </button>
            <button
              onClick={() => setActiveTab('receituario')}
              className={`px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'receituario'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="tab-receituario"
            >
              Receituário
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'queixas' && (
          <div className="space-y-6">
            {/* Botão Nova Queixa / Salvar Queixa */}
            <div>
              <Button
                onClick={() => {
                  if (showNovaQueixa) {
                    // Se a box está aberta, salvar a queixa
                    handleSalvarQueixa();
                  } else {
                    // Se a box está fechada, abrir
                    setShowNovaQueixa(true);
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="btn-nova-queixa"
                disabled={showNovaQueixa && salvandoQueixa}
              >
                {showNovaQueixa ? (salvandoQueixa ? 'Salvando...' : 'Salvar Queixa') : 'Nova Queixa'}
              </Button>
            </div>

            {/* Box Nova Queixa */}
            {showNovaQueixa && (
              <div className="bg-green-50 rounded-lg shadow-lg p-6 border-2 border-green-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Nova Queixa</h3>
                <Textarea
                  value={novaQueixa}
                  onChange={(e) => setNovaQueixa(e.target.value)}
                  rows={8}
                  className="w-full mb-4"
                  placeholder="Descreva a queixa principal do paciente..."
                  data-testid="textarea-nova-queixa"
                />
                <div className="flex justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowNovaQueixa(false);
                      setNovaQueixa('');
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSalvarQueixa}
                    disabled={salvandoQueixa}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="btn-salvar-queixa"
                  >
                    {salvandoQueixa ? 'Salvando...' : 'Salvar Queixa'}
                  </Button>
                </div>
              </div>
            )}

            {/* Timeline de Queixas - APENAS queixas (sem tipo) */}
            <div className="relative">
              {loadingProntuarios ? (
                <div className="text-center py-12" data-testid="loading-queixas-prontuario">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
                  <p className="text-gray-500">Carregando queixas...</p>
                </div>
              ) : (() => {
                const somenteQueixas = prontuariosOrdenados.filter(p => !p.tipo);
                return somenteQueixas.length > 0 ? (
                <div className="space-y-8">
                  {somenteQueixas.map((prontuario, index) => {
                    const { day, month, year, hours, minutes } = formatDateTime(prontuario.data);
                    return (
                      <div key={prontuario.id} className="relative">
                        <div className="flex">
                          <div className="flex flex-col items-center mr-6">
                            <div className="w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-lg z-10">
                              {day}
                            </div>
                            <div className="mt-1 text-sm text-gray-600 font-medium">
                              {getMonthName(month)}/{year}
                            </div>
                          </div>

                          <div className="flex-1 bg-white rounded-lg shadow p-6 border border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="text-base font-bold text-gray-800">Queixa Principal</p>
                                <p className="text-sm text-gray-600 mt-1">{hours}:{minutes}</p>
                              </div>
                              <button
                                onClick={() => handleExcluirQueixa(prontuario.id)}
                                className="text-red-600 hover:text-red-700 text-sm font-medium"
                                data-testid={`btn-excluir-queixa-${prontuario.id}`}
                              >
                                Excluir Queixa
                              </button>
                            </div>
                            <div className="mt-4">
                              <p className="text-gray-900 whitespace-pre-wrap">{prontuario.conteudo}</p>
                            </div>
                            {prontuario.criado_por && (
                              <div className="mt-4 text-xs text-gray-500">
                                Registrado por: {prontuario.criado_por}
                              </div>
                            )}

                            {/* Botão Consultar IA */}
                            <div className="mt-4 pt-3 border-t border-gray-100">
                              {iaHabilitado ? (
                                <Button
                                  size="sm"
                                  onClick={() => handleConsultarIA(prontuario.conteudo)}
                                  disabled={iaLoading && iaQueixaSelecionada === prontuario.conteudo}
                                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-xs"
                                  data-testid={`btn-consultar-ia-${prontuario.id}`}
                                >
                                  <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                                  {iaLoading && iaQueixaSelecionada === prontuario.conteudo ? 'Consultando...' : 'Consultar IA Especialista'}
                                </Button>
                              ) : (
                                <div className="flex items-center gap-3">
                                  <button
                                    disabled
                                    className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                                    data-testid={`btn-ia-premium-${prontuario.id}`}
                                  >
                                    <Lock className="w-3 h-3 mr-1.5" />
                                    Consultar IA Especialista
                                    <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-bold">PREMIUM</span>
                                  </button>
                                  <span className="text-xs text-gray-400">
                                    Entre em contato com o administrador para ativar
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Painel de resposta da IA */}
                            {iaQueixaSelecionada === prontuario.conteudo && iaResposta && (
                              <div className="mt-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-5 border border-purple-200" data-testid="painel-ia-resposta">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <Sparkles className="w-4 h-4 text-purple-600" />
                                    <p className="text-sm font-bold text-purple-800">Assistente IA - Ginecologia</p>
                                  </div>
                                  <button
                                    onClick={() => { setIaResposta(null); setIaQueixaSelecionada(null); }}
                                    className="text-purple-400 hover:text-purple-600"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                                <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed prose prose-sm max-w-none">
                                  {iaResposta.split('**').map((part, i) => 
                                    i % 2 === 1 ? <strong key={i} className="text-purple-900">{part}</strong> : <span key={i}>{part}</span>
                                  )}
                                </div>
                                <p className="mt-4 text-[10px] text-purple-400 italic border-t border-purple-200 pt-2">
                                  Este conteúdo é gerado por inteligência artificial e serve apenas como apoio à decisão clínica. A responsabilidade final é sempre do profissional médico.
                                </p>
                              </div>
                            )}
                            {iaQueixaSelecionada === prontuario.conteudo && iaLoading && (
                              <div className="mt-4 bg-purple-50 rounded-lg p-5 border border-purple-200 flex items-center gap-3" data-testid="ia-loading">
                                <div className="animate-spin w-5 h-5 border-2 border-purple-600 border-t-transparent rounded-full" />
                                <p className="text-sm text-purple-700">Consultando especialista em ginecologia...</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">Nenhuma queixa registrada</p>
                  <p className="text-gray-400 text-sm mt-2">Clique em "Nova Queixa" para adicionar a primeira queixa</p>
                </div>
              );
              })()}
            </div>
          </div>
        )}

        {activeTab === 'arquivos' && (
          <div className="space-y-6">
            {/* Upload Section */}
            <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Enviar Arquivo</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selecione o arquivo (PDF, DOC, JPG, JPEG, PNG - máx 400MB)
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    data-testid="input-file-upload"
                  />
                  {selectedFile && (
                    <p className="mt-2 text-sm text-gray-600">
                      Arquivo selecionado: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descrição (opcional - máx 60 caracteres)
                  </label>
                  <Input
                    value={descricaoArquivo}
                    onChange={(e) => setDescricaoArquivo(e.target.value.slice(0, 60))}
                    placeholder="Ex: Resultado exame de sangue"
                    maxLength={60}
                    className="w-full"
                    data-testid="input-descricao-arquivo"
                  />
                  <p className="mt-1 text-xs text-gray-500">{descricaoArquivo.length}/60 caracteres</p>
                </div>

                {uploading && (
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                    <p className="mt-1 text-sm text-gray-600 text-center">{uploadProgress}%</p>
                  </div>
                )}

                <Button
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="btn-enviar-arquivo"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Enviando...' : 'Enviar Arquivo'}
                </Button>
              </div>
            </div>

            {/* Lista de Arquivos */}
            <div className="relative">
              {arquivosOrdenados.length > 0 ? (
                <div className="space-y-8">
                  {arquivosOrdenados.map((arquivo, index) => {
                    const { day, month, year, hours, minutes } = formatDateTime(arquivo.data_upload);
                    return (
                      <div key={arquivo.id} className="relative">
                        <div className="flex">
                          <div className="flex flex-col items-center mr-6">
                            <div className="w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-lg z-10">
                              {day}
                            </div>
                            <div className="mt-1 text-sm text-gray-600 font-medium">
                              {getMonthName(month)}/{year}
                            </div>
                          </div>

                          <div className="flex-1 bg-white rounded-lg shadow p-6 border border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-start space-x-3">
                                {getFileIcon(arquivo.tipo)}
                                <div>
                                  <p className="text-xs text-gray-500">{arquivo.tipo?.toUpperCase()}</p>
                                  {arquivo.descricao && (
                                    <p className="text-sm text-red-500 mt-1">{arquivo.descricao}</p>
                                  )}
                                  <p className="text-sm text-gray-600">{hours}:{minutes}</p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleDeleteArquivo(arquivo.id)}
                                className="text-red-600 hover:text-red-700 text-sm font-medium"
                                data-testid={`btn-excluir-arquivo-${arquivo.id}`}
                              >
                                Excluir Arquivo
                              </button>
                            </div>
                            
                            <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <p className="text-gray-900 font-medium truncate">{arquivo.nome_original}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {formatFileSize(arquivo.tamanho)}
                                </p>
                              </div>
                              <div className="flex gap-2 w-full sm:w-auto">
                                {['jpg', 'jpeg', 'png', 'pdf'].includes(arquivo.tipo?.toLowerCase()) && (
                                  <Button
                                    onClick={() => handleViewFile(arquivo)}
                                    className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 text-white"
                                    data-testid={`btn-ver-${arquivo.id}`}
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    Ver
                                  </Button>
                                )}
                                <Button
                                  onClick={() => handleDownload(arquivo)}
                                  className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 text-white"
                                  data-testid={`btn-baixar-${arquivo.id}`}
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  Baixar
                                </Button>
                              </div>
                            </div>
                            
                            {arquivo.criado_por && (
                              <div className="mt-4 text-xs text-gray-500">
                                Registrado por: {arquivo.criado_por}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">Nenhum arquivo registrado</p>
                  <p className="text-gray-400 text-sm mt-2">Use o formulário acima para enviar o primeiro arquivo</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab: Solicitar Exames */}
        {activeTab === 'exames' && (() => {
          const isImagem = categoriaExameAtiva === 'imagem';
          const examesLista = isImagem ? examesImagem : examesClinicos;
          const selecionados = isImagem ? examesSelecionadosImagem : examesSelecionadosClinico;
          const manuais = isImagem ? examesManuaisImagem : examesManuaisClinico;
          const novoManual = isImagem ? novoExameManualImagem : novoExameManualClinico;
          const observacoes = isImagem ? observacoesImagem : observacoesClinico;
          const setSelecionados = isImagem ? setExamesSelecionadosImagem : setExamesSelecionadosClinico;
          const setNovoManual = isImagem ? setNovoExameManualImagem : setNovoExameManualClinico;
          const setObservacoes = isImagem ? setObservacoesImagem : setObservacoesClinico;

          const pendenteClinico = novoExameManualClinico.trim();
          const pendenteImagem = novoExameManualImagem.trim();
          const totalClinico = examesSelecionadosClinico.length
            + examesManuaisClinico.length
            + (pendenteClinico && !examesManuaisClinico.includes(pendenteClinico) ? 1 : 0);
          const totalImagem = examesSelecionadosImagem.length
            + examesManuaisImagem.length
            + (pendenteImagem && !examesManuaisImagem.includes(pendenteImagem) ? 1 : 0);
          const totalGeral = totalClinico + totalImagem;
          const qtdGuias = (totalClinico > 0 ? 1 : 0) + (totalImagem > 0 ? 1 : 0);

          const corPrimaria = isImagem ? 'purple' : 'blue';
          const bgPrimaria = isImagem ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700';
          const borderSel = isImagem ? 'border-purple-300 bg-purple-50' : 'border-blue-300 bg-blue-50';
          const bordaObs = isImagem ? 'border-purple-200' : 'border-blue-200';
          const chipBg = isImagem ? 'bg-purple-50 border-purple-200' : 'bg-blue-50 border-blue-200';

          return (
          <div className="space-y-4 pb-24" data-testid="tab-exames-content">
            {/* Sub-tabs Clínicos / Imagem */}
            <div className="flex gap-2">
              <button
                onClick={() => setCategoriaExameAtiva('clinico')}
                className={`flex-1 py-3 px-4 rounded-lg border-2 font-semibold flex items-center justify-center gap-2 transition-colors ${
                  categoriaExameAtiva === 'clinico'
                    ? 'border-blue-600 bg-blue-50 text-blue-700 shadow-sm'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="sub-tab-solicitar-clinicos"
              >
                <span>Exames Clínicos</span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  totalClinico > 0
                    ? 'bg-blue-600 text-white'
                    : (categoriaExameAtiva === 'clinico' ? 'bg-blue-200 text-blue-700' : 'bg-gray-200 text-gray-700')
                }`}>
                  {totalClinico} selecionado{totalClinico !== 1 ? 's' : ''}
                </span>
              </button>
              <button
                onClick={() => setCategoriaExameAtiva('imagem')}
                className={`flex-1 py-3 px-4 rounded-lg border-2 font-semibold flex items-center justify-center gap-2 transition-colors ${
                  categoriaExameAtiva === 'imagem'
                    ? 'border-purple-600 bg-purple-50 text-purple-700 shadow-sm'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="sub-tab-solicitar-imagem"
              >
                <span>Exames de Imagem</span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  totalImagem > 0
                    ? 'bg-purple-600 text-white'
                    : (categoriaExameAtiva === 'imagem' ? 'bg-purple-200 text-purple-700' : 'bg-gray-200 text-gray-700')
                }`}>
                  {totalImagem} selecionado{totalImagem !== 1 ? 's' : ''}
                </span>
              </button>
            </div>

            {/* Exames cadastrados */}
            <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    Exames {isImagem ? 'de Imagem' : 'Clínicos'} Cadastrados
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">Selecione os exames que deseja incluir nesta guia</p>
                </div>
              </div>

              {loadingExames ? (
                <div className="text-center py-6" data-testid="loading-exames-cadastrados">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                  <p className="text-gray-500 text-sm">Carregando exames...</p>
                </div>
              ) : examesLista.length === 0 ? (
                <div className="text-center py-6">
                  <FlaskConical className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">
                    Nenhum exame {isImagem ? 'de imagem' : 'clínico'} cadastrado
                  </p>
                  <p className="text-gray-400 text-xs mt-1">Cadastre exames no menu Configurações</p>
                </div>
              ) : (
                <>
                  {/* Selecionar todos */}
                  <label className="flex items-center gap-3 p-3 mb-3 rounded-lg border border-gray-300 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors" data-testid="selecionar-todos-exames">
                    <input
                      type="checkbox"
                      checked={examesLista.length > 0 && examesLista.every(e => selecionados.includes(e.nome))}
                      onChange={() => {
                        const todosNomes = examesLista.map(e => e.nome);
                        const todosSelecionados = todosNomes.every(n => selecionados.includes(n));
                        setSelecionados(todosSelecionados
                          ? selecionados.filter(n => !todosNomes.includes(n))
                          : [...new Set([...selecionados, ...todosNomes])]);
                      }}
                      className={`w-4 h-4 rounded border-gray-300 focus:ring-${corPrimaria}-500`}
                    />
                    <span className="text-gray-900 text-sm font-medium">Selecionar todos</span>
                  </label>

                  {/* Grid ordenado por coluna */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {(() => {
                      const sorted = [...examesLista].sort((a, b) => a.nome.localeCompare(b.nome));
                      const mid = Math.ceil(sorted.length / 2);
                      const col1 = sorted.slice(0, mid);
                      const col2 = sorted.slice(mid);
                      const ordered = [];
                      for (let i = 0; i < mid; i++) {
                        if (col1[i]) ordered.push(col1[i]);
                        if (col2[i]) ordered.push(col2[i]);
                      }
                      return ordered;
                    })().map((exame) => (
                      <label
                        key={exame.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          selecionados.includes(exame.nome)
                            ? borderSel
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                        data-testid={`exame-checkbox-${exame.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={selecionados.includes(exame.nome)}
                          onChange={() => toggleExameSelecionado(exame.nome)}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <span className="text-gray-900 text-sm whitespace-normal break-words min-w-0 leading-relaxed">{exame.nome}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Exames manuais desta aba */}
            <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-1">
                Adicionar Exame {isImagem ? 'de Imagem' : 'Clínico'} Manual
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Inclua exames {isImagem ? 'de imagem' : 'clínicos'} que não estão na lista cadastrada
              </p>

              <div className="flex gap-3 mb-4">
                <Input
                  value={novoManual}
                  onChange={(e) => setNovoManual(e.target.value)}
                  placeholder={isImagem ? 'Ex: Histerossalpingografia' : 'Nome do exame clínico'}
                  className="flex-1"
                  onBlur={adicionarExameManualAoSairDoCampo}
                  onKeyDown={(e) => e.key === 'Enter' && adicionarExameManual()}
                  data-testid="input-exame-manual"
                />
                <Button
                  onClick={adicionarExameManual}
                  disabled={!novoManual.trim()}
                  className={`${bgPrimaria} text-white`}
                  data-testid="btn-adicionar-exame-manual"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Incluir
                </Button>
              </div>

              {manuais.length > 0 && (
                <div className="space-y-2">
                  {manuais.map((exame, i) => (
                    <div key={i} className={`flex items-start justify-between rounded-lg px-4 py-2 border ${chipBg}`}>
                      <span className="text-sm text-gray-900 whitespace-normal break-words min-w-0 leading-relaxed pr-3">{exame}</span>
                      <button
                        onClick={() => removerExameManual(exame)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`btn-remover-manual-${i}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Observações desta aba */}
            <div className={`bg-white rounded-lg shadow p-6 border-2 ${bordaObs}`}>
              <h3 className="text-lg font-bold text-gray-900 mb-1">
                Observações da Guia — {isImagem ? 'Exames de Imagem' : 'Exames Clínicos'}
              </h3>
              <p className="text-sm text-gray-500 mb-3">
                Este texto aparecerá apenas na guia de exames {isImagem ? 'de imagem' : 'clínicos'}
              </p>
              <RichTextEditor
                value={observacoes}
                onChange={setObservacoes}
                placeholder={isImagem
                  ? 'Ex: USG entre o 5º e 10º dia do ciclo. Comparecer com bexiga cheia.'
                  : 'Ex: Jejum de 12h. Suspender uso de biotina 72h antes.'}
                minHeight={130}
                testId="textarea-observacoes-exame"
              />
            </div>

            {/* Rodapé fixo — resumo e gerar */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 px-6 py-4 shadow-lg z-20">
              <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 flex-wrap">
                <div className="text-sm text-gray-700">
                  <strong className="text-gray-900">
                    {totalGeral} exame{totalGeral !== 1 ? 's' : ''} selecionado{totalGeral !== 1 ? 's' : ''}
                  </strong>
                  {totalGeral > 0 && (
                    <span className="text-gray-500 ml-3">
                      Clínicos: {totalClinico} · Imagem: {totalImagem}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {qtdGuias === 2 && (
                    <div className="text-xs text-gray-500 text-right hidden sm:block">
                      Será gerado<br /><strong className="text-gray-900">1 arquivo com 2 páginas</strong>
                    </div>
                  )}
                  <Button
                    onClick={handleGerarGuia}
                    disabled={gerandoPdf || totalGeral === 0}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 font-semibold"
                    data-testid="btn-gerar-guia"
                  >
                    <Printer className="w-5 h-5 mr-2" />
                    {gerandoPdf
                      ? 'Gerando...'
                      : (qtdGuias === 2 ? 'Gerar Guia (1 arquivo, 2 páginas)' : 'Gerar Guia')}
                  </Button>
                </div>
              </div>
            </div>

          </div>
          );
        })()}

        {/* Receituário Tab */}
        {activeTab === 'receituario' && (
          <div className="space-y-6">
            <div className="flex gap-3">
              <Button
                onClick={handleNovoReceituario}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="btn-novo-receituario"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Receituário
              </Button>
            </div>

            {/* Editor de receituário */}
            {showEditorReceituario && (
              <div className="bg-blue-50 rounded-lg shadow-lg p-6 border-2 border-blue-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {nomeModeloSelecionado ? `Editando: ${nomeModeloSelecionado}` : 'Novo Documento'}
                  </h3>
                  <button
                    onClick={() => { setShowEditorReceituario(false); setConteudoReceituario(''); }}
                    className="text-gray-500 hover:text-gray-700"
                    data-testid="btn-fechar-editor"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo do Documento</label>
                  <select
                    value={tipoReceituario}
                    onChange={(e) => setTipoReceituario(e.target.value)}
                    className="w-full max-w-xs rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="select-tipo-receituario"
                  >
                    <option value="receituario">Receituário</option>
                    <option value="orientacao">Orientação</option>
                    <option value="atestado">Atestado</option>
                    <option value="livre">Modelo Livre</option>
                  </select>
                </div>

                <RichTextEditor
                  value={conteudoReceituario}
                  onChange={setConteudoReceituario}
                  placeholder="Digite a receita, orientação, atestado ou modelo livre aqui..."
                  minHeight={260}
                  testId="textarea-receituario"
                />

                <Button
                  onClick={handleGerarPdfReceituario}
                  disabled={gerandoPdfReceituario || !stripHtml(conteudoReceituario)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-base mt-4"
                  data-testid="btn-gerar-receituario"
                >
                  <Printer className="w-5 h-5 mr-2" />
                  {gerandoPdfReceituario ? 'Gerando PDF...' : 'Gerar PDF'}
                </Button>
              </div>
            )}

            {/* Lista de modelos */}
            {!showEditorReceituario && (
              <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Modelos Cadastrados</h3>
                {loadingModelos ? (
                  <div className="text-center py-8" data-testid="loading-modelos-receituario">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                    <p className="text-gray-500">Carregando modelos...</p>
                  </div>
                ) : modelosReceituario.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Nenhum modelo cadastrado</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Cadastre modelos em Configurações &gt; Receituário e Orientações
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {modelosReceituario.map((modelo) => (
                      <button
                        key={modelo.id}
                        onClick={() => handleSelecionarModelo(modelo)}
                        disabled={modeloSelecionandoId === modelo.id}
                        className="w-full flex items-center justify-between py-3 px-3 hover:bg-blue-50 rounded transition-colors text-left disabled:opacity-60"
                        data-testid={`modelo-receituario-${modelo.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{modelo.nome}</span>
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                              modelo.tipo === 'receituario' ? 'bg-blue-100 text-blue-700' :
                              modelo.tipo === 'orientacao' ? 'bg-green-100 text-green-700' :
                              modelo.tipo === 'livre' ? 'bg-slate-100 text-slate-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {modelo.tipo === 'receituario' ? 'Receituário' :
                               modelo.tipo === 'orientacao' ? 'Orientação' :
                               modelo.tipo === 'livre' ? 'Modelo Livre' : 'Atestado'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 truncate mt-0.5">{stripHtml(modelo.preview || modelo.conteudo || '').substring(0, 80)}...</p>
                        </div>
                        <ChevronLeft className="w-4 h-4 text-gray-400 rotate-180 ml-2 flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Histórico Tab - exibe TUDO: queixas + exames + receituários + orientações + atestados */}
        {activeTab === 'historico' && (
          <div className="relative">
            {loadingProntuarios ? (
              <div className="text-center py-12" data-testid="loading-historico-prontuario">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
                <p className="text-gray-500">Carregando histórico...</p>
              </div>
            ) : prontuariosOrdenados.length > 0 ? (
              <div className="space-y-8">
                {prontuariosOrdenados.map((prontuario) => {
                  const { day, month, year, hours, minutes } = formatDateTime(prontuario.data);
                  return (
                    <div key={prontuario.id} className="relative">
                      <div className="flex">
                        <div className="flex flex-col items-center mr-6">
                          <div className="w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-lg z-10">
                            {day}
                          </div>
                          <div className="mt-1 text-sm text-gray-600 font-medium">
                            {getMonthName(month)}/{year}
                          </div>
                        </div>

                        <div className="flex-1 bg-white rounded-lg shadow p-6 border border-gray-200">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                {prontuario.tipo === 'solicitacao_exames' && (
                                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                    prontuario.categoria === 'imagem'
                                      ? 'bg-purple-100 text-purple-800'
                                      : 'bg-blue-100 text-blue-800'
                                  }`}>
                                    {prontuario.categoria === 'imagem' ? 'Imagem' : 'Clínico'}
                                  </span>
                                )}
                                <p className="text-base font-bold text-gray-800">
                                  {prontuario.tipo === 'solicitacao_exames'
                                    ? (prontuario.categoria === 'imagem'
                                        ? 'Solicitação de Exames de Imagem'
                                        : 'Solicitação de Exames Clínicos')
                                    :
                                   prontuario.tipo === 'documento_receituario' ? 'Receituário' :
                                   prontuario.tipo === 'documento_orientacao' ? 'Orientação Médica' :
                                   prontuario.tipo === 'documento_atestado' ? 'Atestado Médico' :
                                   prontuario.tipo === 'documento_livre' ? 'Modelo Livre' :
                                   'Queixa Principal'}
                                </p>
                              </div>
                              <p className="text-sm text-gray-600 mt-1">{hours}:{minutes}</p>
                            </div>
                          </div>
                          <div className="mt-4">
                            {prontuario.tipo === 'solicitacao_exames' ? (
                              <div>
                                {(() => {
                                  const partes = prontuario.conteudo.split('\n\nObservações:');
                                  const examesStr = partes[0].replace('Exames solicitados: ', '');
                                  const examesList = examesStr.split(', ').filter(e => e.trim());
                                  const observacoes = partes[1]?.trim();
                                  const isImg = prontuario.categoria === 'imagem';
                                  const chipCls = isImg
                                    ? 'bg-purple-50 text-purple-800 border-purple-200'
                                    : 'bg-blue-50 text-blue-800 border-blue-200';
                                  return (
                                    <>
                                      <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                                        {examesList.length} {examesList.length === 1 ? 'exame solicitado' : 'exames solicitados'}
                                      </p>
                                      <div className="flex flex-wrap gap-2">
                                        {examesList.map((exame, idx) => (
                                          <span
                                            key={idx}
                                            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium border ${chipCls}`}
                                          >
                                            {exame.trim()}
                                          </span>
                                        ))}
                                      </div>
                                      {observacoes && (
                                        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                          <p className="text-xs font-medium text-gray-500 mb-1">Observações:</p>
                                          <RichTextDisplay value={observacoes} className="text-sm text-gray-700" />
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            ) : (
                              <RichTextDisplay value={prontuario.conteudo} className="text-gray-900" />
                            )}
                          </div>
                          {prontuario.criado_por && (
                            <div className="mt-4 text-xs text-gray-500">
                              Registrado por: {prontuario.criado_por}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">Nenhum registro no histórico</p>
                <p className="text-gray-400 text-sm mt-2">Registros de queixas, exames, receituários e atestados aparecerão aqui</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Modal Queixa Não Salva */}
      {showSairSemSalvarModal && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          data-testid="modal-queixa-nao-salva-overlay"
        >
          <div
            className="relative bg-white rounded-xl shadow-2xl max-w-md w-full border border-white/70 animate-in fade-in zoom-in-95 duration-200"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-queixa-nao-salva-title"
            aria-describedby="modal-queixa-nao-salva-description"
            data-testid="modal-queixa-nao-salva"
          >
            <button
              type="button"
              onClick={fecharModalQueixaNaoSalva}
              disabled={salvandoQueixa}
              className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors disabled:opacity-50"
              aria-label="Fechar modal e continuar editando"
              data-testid="btn-fechar-modal-queixa-nao-salva"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
            <div className="text-center px-7 pt-8 pb-5">
              <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5 shadow-sm">
                <svg
                  className="w-7 h-7 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M12 9v4m0 4h.01" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M10.29 3.86 1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <h3
                id="modal-queixa-nao-salva-title"
                className="text-xl font-extrabold text-gray-900"
                data-testid="modal-queixa-nao-salva-message"
              >
                A queixa não foi salva.
              </h3>
              <p id="modal-queixa-nao-salva-description" className="sr-only">
                Escolha se deseja salvar a queixa digitada, sair sem salvar ou continuar editando.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-7 pb-3">
              <Button
                variant="outline"
                onClick={confirmarSairSemSalvar}
                disabled={salvandoQueixa}
                className="w-full py-3 bg-transparent border-red-600 text-red-600 hover:bg-red-50 hover:text-red-700 font-bold"
                data-testid="btn-sair-sem-salvar"
              >
                Sair sem salvar
              </Button>
              <Button
                onClick={salvarENavegar}
                disabled={salvandoQueixa}
                className="w-full py-3 bg-green-600 border border-green-600 hover:bg-green-700 hover:border-green-700 text-white font-bold shadow-lg shadow-green-600/20"
                data-testid="btn-salvar-queixa-modal"
              >
                {salvandoQueixa ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
            <div className="px-7 pb-7 text-center">
              <button
                type="button"
                onClick={fecharModalQueixaNaoSalva}
                disabled={salvandoQueixa}
                className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-bold text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-colors disabled:opacity-50"
                data-testid="btn-continuar-editando-queixa"
              >
                Continuar editando
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização de Arquivo */}
      {viewingFile && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex flex-col"
          onClick={closeViewer}
          data-testid="pdf-viewer-modal"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); closeViewer(); }}
            className="fixed right-5 top-20 z-[70] p-3 text-white bg-black/70 hover:bg-black/90 rounded-full transition-colors shadow-lg"
            title="Fechar"
            aria-label="Fechar visualizador de PDF"
            data-testid="btn-close-pdf-viewer"
          >
            <X className="w-6 h-6" />
          </button>
          {/* Header */}
          <div 
            className="flex items-center justify-between p-3 sm:p-4 bg-black/50"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex-1 min-w-0 mr-4">
              <p className="text-white font-medium truncate text-sm sm:text-base">
                {viewingFile.nome_original}
              </p>
              <p className="text-gray-400 text-xs sm:text-sm">
                {viewingFile.tamanho ? formatFileSize(viewingFile.tamanho) : 'PDF gerado'}
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Zoom controls para imagens */}
              {['jpg', 'jpeg', 'png'].includes(viewingFile.tipo?.toLowerCase()) && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleZoomOut(); }}
                    className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                    title="Diminuir zoom"
                  >
                    <ZoomOut className="w-5 h-5" />
                  </button>
                  <span className="text-white text-sm min-w-[50px] text-center">
                    {Math.round(zoom * 100)}%
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleZoomIn(); }}
                    className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                    title="Aumentar zoom"
                  >
                    <ZoomIn className="w-5 h-5" />
                  </button>
                  <div className="w-px h-6 bg-gray-600 mx-2 hidden sm:block" />
                </>
              )}
              
              <button
                onClick={(e) => { e.stopPropagation(); handleDownload(viewingFile); }}
                className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                title="Baixar arquivo"
                data-testid="btn-download-pdf-viewer"
              >
                <Download className="w-5 h-5" />
              </button>
              
              <button
                onClick={(e) => { e.stopPropagation(); closeViewer(); }}
                className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                title="Fechar"
                data-testid="btn-close-pdf-viewer-header"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div 
            className="flex-1 overflow-auto flex items-center justify-center p-4"
            onClick={e => e.stopPropagation()}
          >
            {loadingFile ? (
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                <p className="text-white mt-4">Carregando arquivo...</p>
              </div>
            ) : fileUrl ? (
              <>
                {['jpg', 'jpeg', 'png'].includes(viewingFile.tipo?.toLowerCase()) ? (
                  <div 
                    className="max-w-full max-h-full overflow-auto"
                    style={{ touchAction: 'pan-x pan-y' }}
                  >
                    <img
                      src={fileUrl}
                      alt={viewingFile.nome_original}
                      className="max-w-none transition-transform duration-200"
                      style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
                      onClick={e => e.stopPropagation()}
                    />
                  </div>
                ) : viewingFile.tipo?.toLowerCase() === 'pdf' ? (
                  <iframe
                    src={fileUrl}
                    title={viewingFile.nome_original}
                    className="w-full h-full bg-white rounded-lg"
                    style={{ minHeight: '70vh' }}
                    data-testid="iframe-pdf-viewer"
                  />
                ) : null}
              </>
            ) : (
              <p className="text-white">Erro ao carregar arquivo</p>
            )}
          </div>
          
          {/* Mobile swipe hint */}
          <div className="sm:hidden text-center pb-4">
            <p className="text-gray-400 text-xs">Toque fora da imagem para fechar</p>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default ProntuarioPage;
